/**
 * A kinematic body that walks, sprints and jumps around the hotel.
 *
 * Kinematic rather than dynamic on purpose. This game has no ragdolls and no
 * pushable crates; what it has is a person who must feel *tight* in a corridor
 * twelve feet wide. A rigid body solver gives you sliding, floatiness and
 * sticking on door jambs for free, and you spend the rest of the project tuning
 * them back out. Integrating velocity by hand and pushing out of boxes gives
 * exactly the response the tuning file asks for and nothing else.
 *
 * Three details are doing most of the feel work:
 *
 *   - Gravity is 24 m/s², not 9.81. Real gravity on a 1m jump gives a hang time
 *     of nearly a second, which reads as floating. The jump *height* is the
 *     number a designer cares about, so it is what the data declares, and the
 *     launch velocity is derived from it.
 *   - Coyote time and a jump buffer. A player who presses jump two frames after
 *     walking off a dock edge, or two frames before landing, meant to jump.
 *     Without these the controller is technically correct and feels broken.
 *   - Acceleration, not instant velocity. 34 m/s² reaches walking pace in under
 *     a tenth of a second — indistinguishable from instant at a standing start,
 *     but it means a direction change has weight instead of snapping.
 *
 * Pure: it is handed a broadphase and a movement intent, and never sees a mesh.
 */

import { ceilingHeight, resolveHorizontal, supportHeight } from './collision.js';
import defaults from './feel.json';

export function createCharacter({ world, position = { x: 0, y: 0, z: 0 }, feel = defaults }) {
  const tuning = { ...defaults, ...feel };
  const pos = { ...position };
  const velocity = { x: 0, y: 0, z: 0 };

  let grounded = false;
  let sinceGrounded = Infinity;
  let jumpQueued = Infinity;
  let yaw = 0;
  /** Set by the carry system: a full round table slows you down. */
  let burden = 0;

  /** The launch speed that reaches the declared jump height under this gravity. */
  const jumpVelocity = Math.sqrt(2 * tuning.gravity * tuning.jumpHeight);

  function topSpeed(sprinting, cap) {
    const base = sprinting ? tuning.sprintSpeed : tuning.walkSpeed;
    const penalty = sprinting ? tuning.carrySprintPenalty : tuning.carryWalkPenalty;
    // Burden is 0..1, so half a load costs half the penalty rather than all of
    // it — carrying one chair should not feel like carrying eight.
    const loaded = burden ? base * (1 - (1 - penalty) * Math.min(burden, 1)) : base;
    // A hard ceiling on top, for states that are not about weight at all: you
    // are not carrying anything heavier while you read the sheet, you are
    // simply not looking where you are going.
    return cap ? Math.min(loaded, cap) : loaded;
  }

  /**
   * One step.
   *
   * `intent.move` is already in world space — the camera decides what "forward"
   * means, and the body should not have to know that a camera exists.
   */
  function update(dt, intent = {}) {
    const move = intent.move ?? { x: 0, z: 0 };
    const sprinting = Boolean(intent.sprint) && !intent.blockSprint;
    const wanted = topSpeed(sprinting, intent.speedCap) * (intent.pace ?? 1);

    /*
     * How quickly the body does what it is told. One, ordinarily. Less when
     * something is slowing you down — and it is a scale on the *rates* rather
     * than on the top speed, so a slowed body still gets where it is going, it
     * just takes longer to start, stop and turn. Which is what being slow is.
     */
    const response = intent.response ?? 1;
    const acceleration = (grounded ? tuning.groundAcceleration : tuning.airAcceleration) * response;
    const friction = grounded ? tuning.groundFriction : tuning.airFriction;

    const pushing = Math.hypot(move.x, move.z) > 1e-4;
    if (pushing) {
      const targetX = move.x * wanted;
      const targetZ = move.z * wanted;
      velocity.x = approach(velocity.x, targetX, acceleration * dt);
      velocity.z = approach(velocity.z, targetZ, acceleration * dt);
      yaw = turnToward(yaw, Math.atan2(move.x, move.z), tuning.turnRate * response * dt);
    } else {
      velocity.x = approach(velocity.x, 0, friction * dt);
      velocity.z = approach(velocity.z, 0, friction * dt);
    }

    // Jump is edge-triggered by the caller, but remembered here: a press that
    // arrives a frame early still spends on the next landing.
    if (intent.jump) jumpQueued = 0;
    else jumpQueued += dt;

    const mayJump = grounded || sinceGrounded <= tuning.coyoteTime;
    if (jumpQueued <= tuning.jumpBuffer && mayJump && !intent.blockJump) {
      velocity.y = jumpVelocity;
      grounded = false;
      sinceGrounded = Infinity;
      jumpQueued = Infinity;
    }

    velocity.y -= tuning.gravity * dt;

    // Horizontal first, then vertical. Doing it the other way round lets a body
    // that is mid-step land on the floor it has not arrived over yet.
    const moved = resolveHorizontal(
      { x: pos.x + velocity.x * dt, z: pos.z + velocity.z * dt },
      tuning.radius,
      pos.y,
      pos.y + tuning.height,
      world
    );
    // Killing the velocity into a contact is what stops the body from grinding
    // against a wall and re-penetrating it every frame.
    if (moved.contacts.length) {
      const dx = moved.x - pos.x;
      const dz = moved.z - pos.z;
      if (Math.abs(dx) < 1e-5) velocity.x = 0;
      if (Math.abs(dz) < 1e-5) velocity.z = 0;
    }
    pos.x = moved.x;
    pos.z = moved.z;

    pos.y += velocity.y * dt;

    const floor = supportHeight(pos.x, pos.z, tuning.radius, pos.y, world);
    if (pos.y <= floor && velocity.y <= 0) {
      pos.y = floor;
      velocity.y = 0;
      grounded = true;
      sinceGrounded = 0;
    } else {
      grounded = false;
      sinceGrounded += dt;
      const ceiling = ceilingHeight(pos.x, pos.z, tuning.radius, pos.y + tuning.height, world);
      if (velocity.y > 0 && pos.y + tuning.height > ceiling) {
        pos.y = ceiling - tuning.height;
        velocity.y = 0;
      }
    }

    return { grounded, speed: Math.hypot(velocity.x, velocity.z) };
  }

  return {
    get position() {
      return pos;
    },
    get velocity() {
      return velocity;
    },
    get grounded() {
      return grounded;
    },
    get yaw() {
      return yaw;
    },
    set yaw(value) {
      yaw = value;
    },
    get speed() {
      return Math.hypot(velocity.x, velocity.z);
    },
    get eye() {
      return { x: pos.x, y: pos.y + tuning.eyeHeight, z: pos.z };
    },
    get burden() {
      return burden;
    },
    /** 0..1. The carry system's only lever on how the body moves. */
    set burden(value) {
      burden = Math.max(0, Math.min(1, value));
    },
    tuning,
    update,
    teleport(next) {
      pos.x = next.x;
      pos.y = next.y ?? 0;
      pos.z = next.z;
      velocity.x = 0;
      velocity.y = 0;
      velocity.z = 0;
      grounded = false;
    },
  };
}

/** Moves `value` toward `target` by at most `step`. */
function approach(value, target, step) {
  if (value < target) return Math.min(value + step, target);
  return Math.max(value - step, target);
}

/** Turns an angle toward another by at most `step`, the short way round. */
export function turnToward(from, to, step) {
  let delta = to - from;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  if (Math.abs(delta) <= step) return to;
  return from + Math.sign(delta) * step;
}
